import { useState, useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "../constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LucideTrash,
  LucidePlus,
  LucideBookOpen,
  LucideX,
  LucideEye,
  LucideCheck,
  LucideClipboardList,
  LucideClock,
  LucideFilter,
  LucidePencil,
  LucideCopy,
} from "lucide-react";
import { REPORT_TYPES } from "@/constants/reportTypes";
import ClassList from "@/components/ClassList";
import useMySchools from "@/hooks/useMySchools";

const GRADE_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8"];

function getSchoolYears() {
  const y = new Date().getFullYear();
  return [`${y - 1}–${y}`, `${y}–${y + 1}`, `${y + 1}–${y + 2}`];
}

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background transition-colors focus:outline-none focus:ring-2 " +
  "focus:ring-ring focus:ring-offset-2 text-foreground";

export default function Classes() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { memberships } = useMySchools();

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    grade: "",
    schoolYear: "",
    reportType: "",
    schoolId: "",
  });
  const [creating, setCreating] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", grade: "", schoolYear: "" });
  const [updating, setUpdating] = useState(false);
  const [duplicatingClass, setDuplicatingClass] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const typeListRef = useRef(null);

  const fetchClasses = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch classes");
      setClasses(await res.json());
      setFetchError(null);
    } catch (err) {
      console.error("Error fetching classes:", err);
      setFetchError("Could not load your classes. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When grade changes, re-evaluate visible types and clear invalid selection
  const handleGradeChange = (grade) => {
    const gNum = grade ? parseInt(grade) : null;
    const nowVisible = gNum
      ? REPORT_TYPES.filter((t) => t.grades.includes(gNum))
      : REPORT_TYPES;
    const currentStillValid =
      formData.reportType &&
      nowVisible.some((t) => t.value === formData.reportType && !t.disabled);
    setFormData((p) => ({
      ...p,
      grade,
      reportType: currentStillValid
        ? p.reportType
        : nowVisible.find((t) => !t.disabled)?.value ?? "",
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.grade || !formData.schoolYear || !formData.reportType) return;
    setCreating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Failed to create class");
      const newClass = await res.json();
      setClasses((prev) => [...prev, newClass]);
      setFormData({ name: "", grade: "", schoolYear: "", reportType: "", schoolId: "" });
      setShowModal(false);
      navigate(`/classes/${newClass._id}`);
    } catch (err) {
      console.error("Error creating class:", err);
      toast.error("Failed to create class. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (classItem) => {
    const classLabel = classItem.name ||
      [classItem.grade && `Grade ${classItem.grade}`, classItem.schoolYear].filter(Boolean).join(' · ') ||
      'this class';
    if (
      !confirm(
        `Delete "${classLabel}"? This will also delete all report cards in this class.`
      )
    )
      return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/report-groups/${classItem._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses((prev) => prev.filter((c) => c._id !== classItem._id));
      toast.success("Class deleted.");
    } catch (err) {
      console.error("Error deleting class:", err);
      toast.error("Failed to delete class. Please try again.");
    }
  };

  const handleEdit = (classItem) => {
    setEditingClass(classItem);
    setEditForm({ name: classItem.name, grade: classItem.grade || "", schoolYear: classItem.schoolYear || "" });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups/${editingClass._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error("Failed to update class");
      const updated = await res.json();
      setClasses((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
      setEditingClass(null);
      toast.success("Class updated.");
    } catch (err) {
      console.error("Error updating class:", err);
      toast.error("Failed to update class. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleDuplicateOpen = (classItem) => {
    setDuplicatingClass(classItem);
    const defaultName = classItem.name
      ? `${classItem.name} - Copy`
      : [classItem.grade && `Grade ${classItem.grade}`, classItem.schoolYear].filter(Boolean).join(' · ') + ' - Copy';
    setDuplicateName(defaultName);
  };

  const handleDuplicateSubmit = async (e) => {
    e.preventDefault();
    if (!duplicateName.trim()) return;
    setDuplicating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/report-groups/${duplicatingClass._id}/duplicate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: duplicateName.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw Object.assign(new Error(body.message || "Failed to duplicate class"), { status: res.status });
      }
      const newClass = await res.json();
      setClasses((prev) => [newClass, ...prev]);
      setDuplicatingClass(null);
      toast.success("Class duplicated successfully.");
    } catch (err) {
      console.error("Error duplicating class:", err);
      if (err.status === 403) {
        toast.error(err.message, { duration: 6000 });
      } else {
        toast.error("Failed to duplicate class. Please try again.");
      }
    } finally {
      setDuplicating(false);
    }
  };

  const schoolYears = getSchoolYears();

  // Derive visible report types based on selected grade
  const gradeNum = formData.grade ? parseInt(formData.grade) : null;
  const visibleTypes = gradeNum
    ? REPORT_TYPES.filter((t) => t.grades.includes(gradeNum))
    : REPORT_TYPES;
  const availableCount = visibleTypes.filter((t) => !t.disabled).length;

  const canSubmit = !creating && formData.grade && formData.schoolYear && formData.reportType;

  return (
    <>
      {/* Entrance animation keyframes */}
      <style>{`
        @keyframes typeRowIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .type-row-enter {
          animation: typeRowIn 0.18s ease both;
        }
      `}</style>

      <div className="dashboard-content">
        {/* ── Page header ── */}
        <div className="dashboard-welcome">
          <div>
            <h1 className="dashboard-heading">My Classes</h1>
            <p className="dashboard-subheading">
              Each class holds a group of student report cards.
            </p>
          </div>
          <Button onClick={() => setShowModal(true)} className="gap-2 shrink-0">
            <LucidePlus size={15} />
            New Class
          </Button>
        </div>

        {fetchError && (
          <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {fetchError}
          </div>
        )}

        <ClassList
          items={classes}
          loading={loading}
          emptyIcon={LucideBookOpen}
          emptyTitle="No classes yet"
          emptyMessage="Create your first class to start generating report cards."
          emptyAction={
            <Button onClick={() => setShowModal(true)} variant="outline" className="gap-2">
              <LucidePlus size={14} />
              New Class
            </Button>
          }
          getLinkPath={(item) => `/classes/${item._id}`}
          renderNameExtra={(item) =>
            item.reportsCount !== undefined ? (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full shrink-0">
                {item.reportsCount} {item.reportsCount === 1 ? "report" : "reports"}
              </span>
            ) : null
          }
          renderAction={(item) => (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground hover:bg-muted h-8 w-8 p-0"
                onClick={() => handleEdit(item)}
                title="Edit class"
              >
                <LucidePencil size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground hover:bg-muted h-8 w-8 p-0"
                onClick={() => handleDuplicateOpen(item)}
                title="Duplicate class"
              >
                <LucideCopy size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                onClick={() => handleDelete(item)}
                title="Delete class"
              >
                <LucideTrash size={14} />
              </Button>
            </div>
          )}
        />
      </div>

      {/* ══════════════════════════════════════════════════════
          Create Class Modal
      ══════════════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />

          {/* Dialog - scrollable body */}
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[92vh]">
            {/* Header - fixed */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <LucideClipboardList size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground leading-tight">
                    Create New Class
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Set up a class to start adding students and generating reports.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mt-0.5 shrink-0"
              >
                <LucideX size={15} />
              </button>
            </div>

            <div className="h-px bg-border mx-6 shrink-0" />

            {/* Scrollable form body */}
            <form
              onSubmit={handleCreate}
              className="px-6 py-5 space-y-5 overflow-y-auto"
            >
              {/* Grade + School Year row - required */}
              <div className="flex gap-3">
                {/* Grade - compact */}
                <div className="space-y-1.5 shrink-0" style={{ width: 96 }}>
                  <Label className="text-sm font-medium">
                    Grade{" "}
                    <span className="text-destructive/80 font-normal">*</span>
                  </Label>
                  <select
                    value={formData.grade}
                    onChange={(e) => handleGradeChange(e.target.value)}
                    className={nativeSelect}
                    required
                  >
                    <option value="">-</option>
                    {GRADE_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                {/* School Year */}
                <div className="space-y-1.5 flex-1">
                  <Label className="text-sm font-medium">
                    School Year{" "}
                    <span className="text-destructive/80 font-normal">*</span>
                  </Label>
                  <select
                    value={formData.schoolYear}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, schoolYear: e.target.value }))
                    }
                    className={nativeSelect}
                    required
                  >
                    <option value="">Select year</option>
                    {schoolYears.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Class Name - optional */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-foreground">
                  Class Name{" "}
                  <span className="text-muted-foreground font-normal text-xs">(optional)</span>
                </Label>
                <Input
                  placeholder="e.g. Room 204"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, name: e.target.value }))
                  }
                  className="h-10"
                />
              </div>

              {/* School association - shown only if user has memberships */}
              {memberships.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">
                    School{" "}
                    <span className="text-muted-foreground font-normal text-xs">(optional)</span>
                  </Label>
                  <select
                    value={formData.schoolId}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, schoolId: e.target.value }))
                    }
                    className={nativeSelect}
                  >
                    <option value="">Personal (no school)</option>
                    {memberships.map((m) => {
                      const school = m.school;
                      if (!school) return null;
                      return (
                        <option key={school._id} value={school._id}>
                          {school.name}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Link this class to a school to enable admin oversight and sharing controls.
                  </p>
                </div>
              )}

              {/* ── Report Type ── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Report Type{" "}
                    <span className="text-destructive/80 font-normal">*</span>
                  </Label>

                  {/* Filter indicator - appears when grade is selected */}
                  {gradeNum && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                      <LucideFilter size={10} />
                      {availableCount} available for Grade {formData.grade}
                    </span>
                  )}
                </div>

                {/* Prompt when no grade selected */}
                {!gradeNum && (
                  <p className="text-xs text-muted-foreground/70 italic">
                    Select a grade above to see only compatible report types.
                  </p>
                )}

                {/* Type list */}
                <div ref={typeListRef} className="space-y-2">
                  {visibleTypes.map((type, i) => {
                    const isSelected = formData.reportType === type.value;
                    return (
                      <div
                        key={type.value}
                        role={type.disabled ? undefined : "button"}
                        tabIndex={type.disabled ? undefined : 0}
                        aria-pressed={isSelected}
                        onClick={() =>
                          !type.disabled &&
                          setFormData((p) => ({ ...p, reportType: type.value }))
                        }
                        onKeyDown={(e) => {
                          if (!type.disabled && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            setFormData((p) => ({ ...p, reportType: type.value }));
                          }
                        }}
                        className={[
                          "type-row-enter w-full text-left rounded-xl border px-4 py-3",
                          "transition-all duration-150 flex items-start gap-3",
                          type.disabled
                            ? "opacity-50 cursor-not-allowed border-border bg-muted/20"
                            : isSelected
                            ? "border-primary bg-primary/5 shadow-sm cursor-pointer"
                            : "border-border hover:border-muted-foreground/40 hover:bg-muted/30 cursor-pointer",
                        ].join(" ")}
                        style={{ animationDelay: `${i * 40}ms` }}
                      >
                        {/* Selection indicator */}
                        <div
                          className={[
                            "mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors",
                            type.disabled
                              ? "border-muted-foreground/30"
                              : isSelected
                              ? "border-primary bg-primary"
                              : "border-muted-foreground/40",
                          ].join(" ")}
                        >
                          {isSelected && !type.disabled && (
                            <LucideCheck size={9} className="text-primary-foreground" />
                          )}
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`text-sm font-medium leading-tight ${
                                type.disabled
                                  ? "text-muted-foreground"
                                  : "text-foreground"
                              }`}
                            >
                              {type.label}
                            </span>

                            {/* Grade range badge */}
                            <span className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5 leading-none">
                              {type.subtitle}
                            </span>

                            {/* Coming soon badge */}
                            {type.disabled && (
                              <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground/70 bg-muted/80 rounded px-1.5 py-0.5 leading-none">
                                <LucideClock size={10} />
                                Coming soon
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                            {type.description}
                          </p>
                        </div>

                        {/* Preview button - only for types with a PDF and not disabled */}
                        {type.pdfPreview && !type.disabled && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPdfError(false);
                              setPdfLoading(true);
                              setPreviewPdf(type.pdfPreview);
                            }}
                            title="Preview this report card"
                            className="shrink-0 mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          >
                            <LucideEye size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1 pb-1">
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="flex-1"
                >
                  {creating ? "Creating..." : "Create Class"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          Edit Class Modal
      ══════════════════════════════════════════════════════ */}
      {editingClass && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setEditingClass(null)}
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
            <div className="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <LucidePencil size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground leading-tight">Edit Class</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Update the class name, grade, or school year.</p>
                </div>
              </div>
              <button
                onClick={() => setEditingClass(null)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mt-0.5 shrink-0"
              >
                <LucideX size={15} />
              </button>
            </div>
            <div className="h-px bg-border mx-6 shrink-0" />
            <form onSubmit={handleUpdate} className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">
                  Class Name{" "}
                  <span className="text-muted-foreground font-normal text-xs">(optional)</span>
                </Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                  className="h-10"
                />
              </div>
              <div className="flex gap-3">
                <div className="space-y-1.5 shrink-0" style={{ width: 96 }}>
                  <Label className="text-sm font-medium">Grade</Label>
                  <select
                    value={editForm.grade}
                    onChange={(e) => setEditForm((p) => ({ ...p, grade: e.target.value }))}
                    className={nativeSelect}
                  >
                    <option value="">-</option>
                    {GRADE_OPTIONS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5 flex-1">
                  <Label className="text-sm font-medium">School Year</Label>
                  <select
                    value={editForm.schoolYear}
                    onChange={(e) => setEditForm((p) => ({ ...p, schoolYear: e.target.value }))}
                    className={nativeSelect}
                  >
                    <option value="">Select year</option>
                    {schoolYears.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={updating} className="flex-1">
                  {updating ? "Saving..." : "Save Changes"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditingClass(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          Duplicate Class Modal
      ══════════════════════════════════════════════════════ */}
      {duplicatingClass && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDuplicatingClass(null)}
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
            <div className="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <LucideCopy size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground leading-tight">Duplicate Class</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Creates a copy with all student reports and their data.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setDuplicatingClass(null)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mt-0.5 shrink-0"
              >
                <LucideX size={15} />
              </button>
            </div>
            <div className="h-px bg-border mx-6 shrink-0" />
            <form onSubmit={handleDuplicateSubmit} className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">
                  New Class Name <span className="text-destructive/80 font-normal">*</span>
                </Label>
                <Input
                  value={duplicateName}
                  onChange={(e) => setDuplicateName(e.target.value)}
                  required
                  className="h-10"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={duplicating || !duplicateName.trim()} className="flex-1">
                  {duplicating ? "Duplicating..." : "Duplicate"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDuplicatingClass(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          PDF Preview Modal
      ══════════════════════════════════════════════════════ */}
      {previewPdf && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => { setPreviewPdf(null); setPdfError(false); setPdfLoading(false); }}
          />
          <div
            className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden"
            style={{ height: "82vh" }}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
              <p className="text-sm font-medium text-foreground">
                Report Card Preview
              </p>
              <button
                onClick={() => { setPreviewPdf(null); setPdfError(false); setPdfLoading(false); }}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <LucideX size={15} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden relative">
              {pdfLoading && !pdfError && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted/30 z-10">
                  <p className="text-sm text-muted-foreground">Loading preview…</p>
                </div>
              )}
              {pdfError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-5 py-4 text-center max-w-xs">
                    <p className="text-sm font-medium text-destructive">Preview unavailable</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Could not load the report card preview.
                    </p>
                  </div>
                </div>
              ) : (
                <iframe
                  key={previewPdf}
                  src={previewPdf}
                  className="w-full h-full border-0"
                  title="Report card preview"
                  onLoad={() => setPdfLoading(false)}
                  onError={() => { setPdfLoading(false); setPdfError(true); }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
